import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Copy, Check, WhatsappLogo, FacebookLogo, InstagramLogo, TiktokLogo, Users, Trophy, ArrowRight } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ReferralsPage — rebuilt from scratch (ITERAZIONE 13, Task 13).
 *
 *   Layout (mobile-first):
 *     1) Title + 4-step numbered explainer
 *     2) ONE primary CTA: "Kopjo Linkun" (single shareable URL with ?ref=code)
 *     3) ONE row of social-share icons: WhatsApp / Facebook / Instagram / TikTok
 *     4) Compact stats block (friends invited, rewards earned)
 *
 *   Backend stays the same (referral logic in server.py) — the friend gets 1 Standard wash
 *   free after their FIRST PAID wash; the inviter gets 1 Standard wash every 5 friends who
 *   completed at least one paid wash (max 10 rewards/month).
 */
export default function ReferralsPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [referralData, setReferralData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/referrals/my`, { withCredentials: true })
      .then((r) => { if (!cancelled) setReferralData(r.data); })
      .catch((err) => console.error(err));
    return () => { cancelled = true; };
  }, []);

  const referralCode = referralData?.referral_code
    || (user?.user_id ? `AUTOSPA${user.user_id.slice(-6).toUpperCase()}` : 'AUTOSPA5');
  // Single deep-link that pre-fills the referral code on the registration page
  // (RegisterPage reads ?ref=… from location.search on mount).
  const referralLink = `${window.location.origin}/registrohu?ref=${referralCode}`;

  const stats = {
    friendsInvited: referralData?.friends_invited ?? 0,
    rewardsEarned: referralData?.rewards_earned ?? 0,
    nextRewardAt: referralData?.next_reward_at ?? 5,
  };

  // Resilient clipboard — handles browsers / iframes without the Async Clipboard API.
  const copyText = async (text) => {
    if (!text) return false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) { console.debug('[Referrals] share fallback', err?.message); }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (_) {
      return false;
    }
  };

  const handleCopyLink = async () => {
    const ok = await copyText(referralLink);
    if (ok) {
      setCopied(true);
      toast.success(t('copiedShort'));
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error(t('copyFailed') || 'Copy failed');
    }
  };

  const handleShare = async (platform) => {
    const banner = t('referralBannerText');
    const message = `${banner}\n\n${referralLink}`;

    // Instagram + TikTok don't have web share URLs → always copy the message + link.
    if (platform === 'instagram' || platform === 'tiktok') {
      const ok = await copyText(message);
      if (ok) toast.success(t('referralBannerShared'));
      else toast.error(t('copyFailed') || 'Copy failed');
      return;
    }

    let url;
    if (platform === 'whatsapp') {
      url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    } else if (platform === 'facebook') {
      url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}&quote=${encodeURIComponent(banner)}`;
    } else {
      return;
    }

    // Web Share API on mobile gives a native share sheet — fall back to window.open.
    try {
      if (platform === 'whatsapp' && navigator?.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
        await navigator.share({ text: message, url: referralLink });
        return;
      }
    } catch (err) { console.debug('[Referrals] share dismissed/unsupported', err?.message); }

    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
      // Popup blocked → last-resort: copy the full message.
      const ok = await copyText(message);
      if (ok) toast.success(t('referralBannerShared'));
    }
  };

  const steps = [
    { n: 1, title: t('referralStepNew1Title'), desc: t('referralStepNew1Desc') },
    { n: 2, title: t('referralStepNew2Title'), desc: t('referralStepNew2Desc') },
    { n: 3, title: t('referralStepNew3Title'), desc: t('referralStepNew3Desc') },
    { n: 4, title: t('referralStepNew4Title'), desc: t('referralStepNew4Desc') },
  ];

  const socials = [
    { id: 'whatsapp', Icon: WhatsappLogo, bg: '#25D36620', color: '#25D366', label: 'WhatsApp' },
    { id: 'facebook', Icon: FacebookLogo, bg: '#1877F220', color: '#1877F2', label: 'Facebook' },
    { id: 'instagram', Icon: InstagramLogo, bg: '#E4405F20', color: '#E4405F', label: 'Instagram' },
    { id: 'tiktok',   Icon: TiktokLogo,   bg: '#ffffff15', color: '#ffffff', label: 'TikTok' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] pb-24 safe-bottom" data-testid="referrals-page">
      {/* Title */}
      <motion.div
        className="px-4 pt-6 pb-3"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold font-['Outfit'] text-white leading-tight">
          {t('inviteFriendsTitle')}
        </h1>
        <p className="text-white/55 text-sm mt-1">{t('inviteFriendsLead')}</p>
      </motion.div>

      {/* Numbered steps */}
      <div className="px-4 space-y-2 mb-5" data-testid="referral-steps-block">
        {steps.map(({ n, title, desc }, i) => (
          <motion.div
            key={n}
            className="p-3.5 rounded-2xl bg-[#0a0a0a] border border-white/5 flex gap-3"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.06 * i }}
            data-testid={`referral-step-${n}`}
          >
            <div
              className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm font-['Outfit']"
              style={{ background: '#FF5A0018', color: '#FF5A00' }}
            >
              {n}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white leading-snug">{title}</p>
              <p className="text-[12px] text-white/55 leading-snug mt-0.5">{desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Single copy-link CTA */}
      <div className="px-4 mb-4">
        <Button
          onClick={handleCopyLink}
          className="w-full h-14 bg-[#0ABAB5] hover:bg-[#089994] text-black font-semibold text-base rounded-2xl shadow-[0_0_24px_rgba(10,186,181,0.35)] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          data-testid="copy-link-btn"
        >
          {copied ? <Check size={20} weight="bold" /> : <Copy size={20} weight="bold" />}
          {copied ? t('copiedShort') : t('copyLink')}
        </Button>
        <p className="text-[11px] text-white/40 text-center mt-2 font-mono break-all px-2" data-testid="referral-link-preview">
          {referralLink}
        </p>
      </div>

      {/* Single row of 4 social shares */}
      <div className="px-4 mb-6">
        <p className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-2 font-semibold text-center">
          {t('shareWith')}
        </p>
        <div className="grid grid-cols-4 gap-2" data-testid="referral-social-row">
          {socials.map(({ id, Icon, bg, color, label }) => (
            <button
              key={id}
              onClick={() => handleShare(id)}
              className="h-14 rounded-2xl flex flex-col items-center justify-center gap-0.5 active:scale-95 transition-transform"
              style={{ background: bg, color }}
              data-testid={`banner-share-${id}`}
              aria-label={label}
            >
              <Icon size={20} weight="fill" />
              <span className="text-[10px] font-semibold">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Stats — compact */}
      <div className="px-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="p-4 rounded-2xl bg-[#0a0a0a] border border-white/5">
            <div className="flex items-center gap-2 mb-1.5">
              <Users size={16} weight="duotone" className="text-[#0ABAB5]" />
              <span className="text-[10px] uppercase tracking-wider text-white/45 font-semibold">
                {t('friendsInvited')}
              </span>
            </div>
            <p className="text-3xl font-bold font-['Outfit'] text-white">
              {stats.friendsInvited}
            </p>
            {stats.friendsInvited < stats.nextRewardAt && (
              <p className="text-[11px] text-white/50 mt-0.5">
                {stats.nextRewardAt - stats.friendsInvited} {t('toNextReward')}
              </p>
            )}
          </div>
          <div className="p-4 rounded-2xl bg-gradient-to-br from-[#FF5A00]/10 to-transparent border border-[#FF5A00]/25">
            <div className="flex items-center gap-2 mb-1.5">
              <Trophy size={16} weight="duotone" className="text-[#FF5A00]" />
              <span className="text-[10px] uppercase tracking-wider text-white/45 font-semibold">
                {t('rewardsEarned')}
              </span>
            </div>
            <p className="text-3xl font-bold font-['Outfit'] text-white">
              {stats.rewardsEarned}
            </p>
            <p className="text-[11px] text-[#FF5A00]/80 mt-0.5">{t('standardWashesFree')}</p>
          </div>
        </div>

        {/* Progress to next reward */}
        {stats.friendsInvited < stats.nextRewardAt && (
          <div className="mt-3 p-4 rounded-2xl bg-[#0a0a0a] border border-white/5" data-testid="referral-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px] text-white/60">
                {t('progressToNextReward')}
              </span>
              <span className="text-[12px] text-[#FF5A00] font-semibold">
                {stats.friendsInvited}/{stats.nextRewardAt}
              </span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#FF5A00] to-[#FFD700] transition-all"
                style={{ width: `${(stats.friendsInvited / stats.nextRewardAt) * 100}%` }}
              />
            </div>
            <p className="text-[11px] text-white/50 mt-2 flex items-center gap-1">
              <ArrowRight size={12} className="text-[#FF5A00]" />
              {t('keepInviting')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
